import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { PartialDeltaSyncEngine, SyncResult, SyncOptions } from '../services/PartialDeltaSyncEngine';
import { InMemorySourceOfTruth } from '../services/InMemorySourceOfTruth';
import { DocumentPersistenceManager } from '../services/DocumentPersistenceManager';
import { SQLiteService } from '../services/SQLiteService';
import { serverUrl as defaultServerUrl } from '../config/environment';

export interface PartialDeltaSyncState {
  isStructuralSyncing: boolean;
  isFetchingSubtree: boolean;
  lastSyncTimestamp: number;
  syncInProgress: boolean;
  persistenceInProgress: boolean;
  lastPersistenceTimestamp: number;
}

export interface UsePartialDeltaSyncResult {
  // Core instances (null until dependency is injected)
  sourceOfTruth: InMemorySourceOfTruth | null;
  syncEngine: PartialDeltaSyncEngine | null;
  persistenceManager: DocumentPersistenceManager | null;
  
  // State
  syncState: PartialDeltaSyncState;
  
  // Sync methods
  sync: (options?: SyncOptions) => Promise<SyncResult>;
  fetchSubtreeIfNeeded: (objectId: string) => Promise<SyncResult>;
  
  // Lazy loading methods
  loadPageSubtreeIfNeeded: (pageId: string) => Promise<{
    loadedFromLocal: number;
    loadedFromRemote: number;
    alreadyLoaded: boolean;
  }>;
  
  // Document loading methods
  loadDocument: (options?: { 
    loadFromDisk?: boolean; 
    partialLoad?: boolean; 
    maxDepth?: number 
  }) => Promise<number>; // returns number of properties loaded
  checkDocumentExists: () => Promise<boolean>;
  
  // Document management methods
  deleteDocumentFromRemote: () => Promise<boolean>;
  
  // Persistence methods
  persistToDatabase: () => Promise<void>;
  
  // Utility methods
  isDocumentLoaded: () => boolean;
  getPropertyCount: () => number;
  getPendingPersistenceCount: () => number;
}

/**
 * React hook for Partial Delta Sync Engine with RFC-compliant architecture
 * 
 * Provides:
 * - In-memory source of truth for all document properties
 * - Recursive depth-based sync with remote server
 * - Automatic persistence to local SQLite database
 * - State management for React components
 */
export function usePartialDeltaSync(
  documentId: string,
  sqliteService: SQLiteService,
  sourceOfTruth: InMemorySourceOfTruth | null,
  serverUrl: string = defaultServerUrl,
  stateManager?: { markClean: () => void; refreshState?: () => void } | null
): UsePartialDeltaSyncResult {
  
  // State management
  const [syncState, setSyncState] = useState<PartialDeltaSyncState>({
    isStructuralSyncing: false,
    isFetchingSubtree: false,
    lastSyncTimestamp: 0,
    syncInProgress: false,
    persistenceInProgress: false,
    lastPersistenceTimestamp: 0
  });

  // Refs for stable instances and state tracking
  const sourceOfTruthRef = useRef<InMemorySourceOfTruth | null>(null);
  const syncEngineRef = useRef<PartialDeltaSyncEngine | null>(null);
  const persistenceManagerRef = useRef<DocumentPersistenceManager | null>(null);
  const syncInProgressRef = useRef<boolean>(false);
  const persistenceInProgressRef = useRef<boolean>(false);

  // Use the injected sourceOfTruth dependency (wait for it to be available)
  const sourceOfTruthInstance = useMemo(() => {
    if (!sourceOfTruth) {
      return null;
    }
    
    sourceOfTruthRef.current = sourceOfTruth;
    return sourceOfTruth;
  }, [sourceOfTruth]);

  const syncEngine = useMemo(() => {
    if (!sourceOfTruthInstance) {
      return null; // Wait for dependency injection
    }
    
    if (!syncEngineRef.current) {
      syncEngineRef.current = new PartialDeltaSyncEngine(
        sourceOfTruthInstance,
        serverUrl,
        documentId,
        (isStructuralSyncing: boolean) => {
          setSyncState(prev => ({ ...prev, isStructuralSyncing }));
        }
      );
    }
    return syncEngineRef.current;
  }, [sourceOfTruthInstance, serverUrl, documentId]);

  const persistenceManager = useMemo(() => {
    if (!sourceOfTruthInstance) {
      return null; // Wait for dependency injection
    }
    
    if (!persistenceManagerRef.current) {
      persistenceManagerRef.current = new DocumentPersistenceManager(
        sourceOfTruthInstance,
        sqliteService,
        documentId
      );
    }
    return persistenceManagerRef.current;
  }, [sourceOfTruthInstance, sqliteService, documentId]);

  // Sync method with state management
  const sync = useCallback(async (options: SyncOptions = {}): Promise<SyncResult> => {
    if (!syncEngine) {
      console.warn('🔄 [PARTIAL DELTA SYNC] SyncEngine not ready (waiting for dependency injection)');
      return { pushedCount: 0, pulledCount: 0 };
    }
    
    if (syncInProgressRef.current) {
      console.warn('🔄 [PARTIAL DELTA SYNC] Sync already in progress, skipping');
      return { pushedCount: 0, pulledCount: 0 };
    }

    syncInProgressRef.current = true;
    setSyncState(prev => ({ 
      ...prev, 
      isStructuralSyncing: options.isStructuralSync === true, 
      syncInProgress: true 
    }));

    try {
      const result = await syncEngine.sync(options);
      
      setSyncState(prev => ({ 
        ...prev, 
        lastSyncTimestamp: Date.now() 
      }));

      // Notify state manager to refresh UI after sync
      if (result && (result.pulledCount > 0 || result.pushedCount > 0)) {
        stateManager?.refreshState?.();
      }

      // Auto-persist after successful sync
      if (result && result.pulledCount > 0) {
        setTimeout(() => persistToDatabase(), 1000);
      }

      return result || { pushedCount: 0, pulledCount: 0 };
    } catch (error) {
      console.error('🔄 [PARTIAL DELTA SYNC] Sync failed:', error);
      throw error;
    } finally {
      syncInProgressRef.current = false;
      setSyncState(prev => ({ 
        ...prev, 
        isStructuralSyncing: false, 
        syncInProgress: false 
      }));
    }
  }, [syncEngine]);

  // Subtree fetch with state management
  const fetchSubtreeIfNeeded = useCallback(async (objectId: string): Promise<SyncResult> => {
    if (!syncEngine) {
      console.warn('🔄 [PARTIAL DELTA SYNC] SyncEngine not ready (waiting for dependency injection)');
      return { pushedCount: 0, pulledCount: 0 };
    }
    
    setSyncState(prev => ({ ...prev, isFetchingSubtree: true }));

    try {
      const result = await syncEngine.fetchSubtreeIfNeeded(objectId);
      
      // Auto-persist if we fetched new content
      if (result.pulledCount > 0) {
        setTimeout(() => persistToDatabase(), 500);
      }

      return result;
    } catch (error) {
      console.error('🌳 [PARTIAL DELTA SYNC] Subtree fetch failed:', error);
      throw error;
    } finally {
      setSyncState(prev => ({ ...prev, isFetchingSubtree: false }));
    }
  }, [syncEngine]);

  // Lightweight document existence check
  const checkDocumentExists = useCallback(async (): Promise<boolean> => {
    if (!syncEngine) {
      console.warn('Sync engine not available for document existence check');
      return false;
    }
    return await syncEngine.checkDocumentExists();
  }, [syncEngine]);

  // Delete document from remote server
  const deleteDocumentFromRemote = useCallback(async (): Promise<boolean> => {
    if (!syncEngine) {
      console.warn('Sync engine not available for remote deletion');
      return false;
    }
    return await syncEngine.deleteDocumentFromRemote();
  }, [syncEngine]);

  // Document loading method
  const loadDocument = useCallback(async (options: {
    loadFromDisk?: boolean;
    partialLoad?: boolean;
    maxDepth?: number;
  } = {}): Promise<number> => {
    const { loadFromDisk = true, partialLoad = true, maxDepth = 2 } = options;
    
    
    let propertiesLoaded = 0;

    // Step 1: Load from local database if requested
    if (loadFromDisk) {
      try {
        propertiesLoaded = await persistenceManager?.loadPropertiesFromDisk() || 0;
      } catch (error) {
        console.warn('📖 [PARTIAL DELTA SYNC] Failed to load from disk:', error);
      }
    }

    // Step 2: If partial load, sync structural properties only
    if (partialLoad) {
      try {
        const syncResult = await sync({ 
          maxDepth, 
          fetchUnknownSubtrees: false,
          isStructuralSync: true
        });
        propertiesLoaded += syncResult.pulledCount;
      } catch (error) {
        console.warn('📖 [PARTIAL DELTA SYNC] Failed to load structural properties:', error);
      }
    }

    return propertiesLoaded;
  }, [documentId, persistenceManager, sync]);

  // Persistence method with state management  
  const persistToDatabase = useCallback(async (): Promise<void> => {
    // Check current state to prevent concurrent operations
    if (persistenceInProgressRef.current) {
      return;
    }

    if (!persistenceManager?.isPersistenceNeeded()) {
      return;
    }

    persistenceInProgressRef.current = true;
    setSyncState(prev => ({ ...prev, persistenceInProgress: true }));

    try {
      await persistenceManager?.persistPendingProperties();
      setSyncState(prev => ({ 
        ...prev, 
        lastPersistenceTimestamp: Date.now() 
      }));
      
      // Mark document as clean after successful automatic persistence
      if (stateManager?.markClean) {
        stateManager.markClean();
      }
    } catch (error) {
      console.error('💾 [PARTIAL DELTA SYNC] Persistence failed:', error);
      throw error;
    } finally {
      persistenceInProgressRef.current = false;
      setSyncState(prev => ({ ...prev, persistenceInProgress: false }));
    }
  }, [persistenceManager, stateManager]);

  // Utility methods
  const isDocumentLoaded = useCallback((): boolean => {
    if (!sourceOfTruthInstance) return false;
    return sourceOfTruthInstance.getObjectCount() > 0;
  }, [sourceOfTruthInstance]);

  const getPropertyCount = useCallback((): number => {
    if (!sourceOfTruthInstance) return 0;
    return sourceOfTruthInstance.getPropertyCount();
  }, [sourceOfTruthInstance]);

  const getPendingPersistenceCount = useCallback((): number => {
    if (!persistenceManager) return 0;
    return persistenceManager.getPropertiesSinceLastPersistence().length;
  }, [persistenceManager]);

  // Auto-persistence effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (persistenceManager?.isPersistenceNeeded() && !syncState.persistenceInProgress) {
        persistToDatabase().catch(error => 
          console.error('💾 [PARTIAL DELTA SYNC] Auto-persistence failed:', error)
        );
      }
    }, 5000); // Auto-persist every 5 seconds if needed

    return () => clearInterval(interval);
  }, [persistenceManager, persistToDatabase, syncState.persistenceInProgress]);

  // Track which pages have been processed to prevent duplicates
  const processedPagesRef = useRef<Set<string>>(new Set());

  // Lazy loading method for page subtrees
  const loadPageSubtreeIfNeeded = useCallback(async (pageId: string) => {
    
    // Check if we've already processed this page in this session
    if (processedPagesRef.current.has(pageId)) {
      return {
        loadedFromLocal: 0,
        loadedFromRemote: 0,
        alreadyLoaded: true
      };
    }
    
    // Check if subtree is already loaded by looking for text blocks
    const existingChildren = sourceOfTruthInstance?.getChildObjects(pageId) || [];
    const hasTextBlocks = existingChildren.some((childId: string) => {
      const metadata = sourceOfTruthInstance?.getObjectMetadata(childId);
      return metadata?.objectType === 'text_block';
    });
    
    if (hasTextBlocks) {
      processedPagesRef.current.add(pageId); // Mark as processed
      return {
        loadedFromLocal: 0,
        loadedFromRemote: 0,
        alreadyLoaded: true
      };
    }
    
    
    // Load from local database first
    const localCount = await persistenceManager?.loadSubtreePropertiesFromDisk(pageId) || 0;
    
    // Check if we still have no content (subtree hash would be 0)
    const childrenAfterLocal = sourceOfTruthInstance?.getChildObjects(pageId) || [];
    const hasContentAfterLocal = childrenAfterLocal.length > 0;
    
    let remoteCount = 0;
    if (!hasContentAfterLocal && localCount === 0) {
      try {
        const syncResult = await fetchSubtreeIfNeeded(pageId);
        remoteCount = syncResult.pulledCount || 0;
      } catch (error) {
        console.warn(`❌ [LAZY LOADING] Failed to fetch remote content for page ${pageId}:`, error);
      }
    }
    
    
    // Mark this page as processed to prevent duplicate loading
    processedPagesRef.current.add(pageId);
    
    // Notify state manager to refresh UI after lazy loading
    if (remoteCount > 0) {
      stateManager?.refreshState?.();
    }
    
    return {
      loadedFromLocal: localCount,
      loadedFromRemote: remoteCount,
      alreadyLoaded: false
    };
  }, [sourceOfTruthInstance, persistenceManager, fetchSubtreeIfNeeded]);

  return {
    // Core instances
    sourceOfTruth: sourceOfTruthInstance,
    syncEngine,
    persistenceManager,
    
    // State
    syncState,
    
    // Sync methods
    sync,
    fetchSubtreeIfNeeded,
    
    // Lazy loading methods
    loadPageSubtreeIfNeeded,
    
    // Document loading
    loadDocument,
    checkDocumentExists,
    
    // Document management
    deleteDocumentFromRemote,
    
    // Persistence
    persistToDatabase,
    
    // Utilities
    isDocumentLoaded,
    getPropertyCount,
    getPendingPersistenceCount
  };
}
